import { useMemo, useState } from 'react'
import { Button, Input, message, Segmented, Select, Space, Tooltip, Typography } from 'antd'
import { cryptoTransform } from '@/api/dataFactory'
import type { CryptoAlgorithm } from '@/types'

const { Text } = Typography

/** 算法分组选项（编码 / 哈希 / HMAC / AES） */
const ALGORITHM_GROUPS: { label: string; options: { value: CryptoAlgorithm; label: string }[] }[] = [
  {
    label: '编码',
    options: [
      { value: 'base64_encode', label: 'Base64 编码' },
      { value: 'base64_decode', label: 'Base64 解码' },
      { value: 'url_encode', label: 'URL 编码' },
      { value: 'url_decode', label: 'URL 解码' },
    ],
  },
  {
    label: '哈希',
    options: [
      { value: 'md5', label: 'MD5' },
      { value: 'sha1', label: 'SHA1' },
      { value: 'sha256', label: 'SHA256' },
      { value: 'sha512', label: 'SHA512' },
    ],
  },
  {
    label: 'HMAC',
    options: [
      { value: 'hmac_sha256', label: 'HMAC-SHA256' },
      { value: 'hmac_sha1', label: 'HMAC-SHA1' },
      { value: 'hmac_md5', label: 'HMAC-MD5' },
    ],
  },
  {
    label: 'AES',
    options: [
      { value: 'aes_encrypt', label: 'AES 加密' },
      { value: 'aes_decrypt', label: 'AES 解密' },
    ],
  },
]

const ALL_ALGORITHMS = ALGORITHM_GROUPS.flatMap((g) => g.options)
const findAlgorithmLabel = (v: CryptoAlgorithm) => ALL_ALGORITHMS.find((a) => a.value === v)?.label ?? v

/** 需要 key 的算法（HMAC/AES） */
const KEY_REQUIRED: CryptoAlgorithm[] = ['hmac_sha256', 'hmac_sha1', 'hmac_md5', 'aes_encrypt', 'aes_decrypt']

/** 编码与加解密页签：测签名各接口的刚需（Base64/URL/MD5/SHA/HMAC/AES） */
export default function CryptoTab() {
  const [algorithm, setAlgorithm] = useState<CryptoAlgorithm>('base64_encode')
  const [input, setInput] = useState('')
  const [key, setKey] = useState('')
  const [aesMode, setAesMode] = useState<'CBC' | 'ECB'>('ECB')
  const [iv, setIv] = useState('')
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  const needKey = useMemo(() => KEY_REQUIRED.includes(algorithm), [algorithm])
  const isAes = useMemo(() => algorithm.startsWith('aes_'), [algorithm])
  const isHash = useMemo(() => ['md5', 'sha1', 'sha256', 'sha512'].includes(algorithm), [algorithm])

  /** 执行转换 */
  const handleRun = async () => {
    if (!input) {
      message.warning('请输入待处理内容')
      return
    }
    if (needKey && !key) {
      message.warning('该算法需要密钥 key')
      return
    }
    if (isAes && aesMode === 'CBC' && !iv) {
      message.warning('CBC 模式需要 16 字节 IV')
      return
    }
    setLoading(true)
    try {
      const res = await cryptoTransform({ algorithm, input, key: key || undefined, aes_mode: aesMode, iv: iv || undefined })
      setResult(res.result)
      message.success('转换成功')
    } finally {
      setLoading(false)
    }
  }

  /** 结果放回输入（配合解码方向连续调试） */
  const handleSwap = () => {
    setInput(result)
    setResult('')
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(result)
    message.success('已复制结果')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px' }}>
      {/* 算法选择 */}
      <Space wrap style={{ flexShrink: 0 }}>
        <Select
          value={algorithm}
          onChange={setAlgorithm}
          style={{ width: 180 }}
          options={ALGORITHM_GROUPS.map((g) => ({ label: g.label, options: g.options }))}
        />
        {needKey && (
          <Input.Password
            placeholder={isAes ? 'AES 密钥（16/24/32 字节）' : 'HMAC 密钥'}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            style={{ width: 280 }}
          />
        )}
        {isAes && (
          <>
            <Segmented
              value={aesMode}
              onChange={(v) => setAesMode(v as 'CBC' | 'ECB')}
              options={[{ value: 'ECB', label: 'ECB' }, { value: 'CBC', label: 'CBC' }]}
            />
            {aesMode === 'CBC' && (
              <Input
                placeholder="IV（16 字节）"
                value={iv}
                onChange={(e) => setIv(e.target.value)}
                style={{ width: 240 }}
              />
            )}
          </>
        )}
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading}
          onClick={handleRun}
        >
          执行
        </Button>
      </Space>

      {/* 输入区 */}
      <Input.TextArea
        placeholder={isAes && algorithm === 'aes_decrypt' ? '粘贴 Base64 密文…' : '输入原文（URL/查询参数/Header/报文均可）…'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={7}
        style={{ flexShrink: 0, fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }}
      />

      {/* 输出区 */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Space style={{ marginBottom: 8, flexShrink: 0 }}>
          <Text strong>{findAlgorithmLabel(algorithm)} 结果</Text>
          {isHash && <Text type="secondary" style={{ fontSize: 12 }}>(哈希不可逆，输出 hex 小写)</Text>}
          <div style={{ flex: 1 }} />
          <Tooltip title="将结果放回输入框（解码方向连续调试）">
            <Button size="small" disabled={!result} onClick={handleSwap}>回填输入</Button>
          </Tooltip>
          <Button size="small" disabled={!result} onClick={handleCopy}>复制</Button>
        </Space>
        <Input.TextArea
          readOnly
          value={result}
          placeholder="结果将显示在这里…"
          style={{
            flex: 1,
            minHeight: 0,
            fontFamily: "'JetBrains Mono', 'Consolas', monospace",
            fontSize: 12,
            background: 'var(--au-bg-cream)',
            resize: 'none',
          }}
        />
      </div>
    </div>
  )
}
